import { FeeAmount } from '@kodiak-finance/v3-sdk'
import { AutoColumn } from 'components/Column'
import * as T from 'components/Typography'
import { useFeeTierDistribution } from 'hooks/useFeeTierDistribution'
import { PoolState } from 'hooks/usePools'
import styled from 'styled-components'
import { ThemedText } from 'theme'

import { FEE_AMOUNT_DETAIL } from './shared'

/** normalize → 0..100 once (avoid double-multiply) */
const toDisplayPercent = (value?: number | null, alreadyPercent = true): number | undefined => {
  if (value == null || Number.isNaN(value)) return undefined
  const pct = alreadyPercent ? value : value * 100
  return Math.max(0, Math.min(100, pct))
}

const Chip = styled.button<{ active: boolean; disabled?: boolean }>`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  min-height: 56px;
  border-radius: 12px;
  padding: 8px 10px;

  background: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ active, theme }) => (active ? theme.deprecated_primary1 : theme.cardBorderPrimary)};
  box-shadow: ${({ active, theme }) => (active ? `0 0 0 2px ${theme.deprecated_primary1}33 inset` : 'none')};

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    filter: ${({ disabled }) => (disabled ? 'none' : 'brightness(1.05)')};
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.deprecated_primary1};
  }

  ${({ theme }) => theme.mediaWidth.upToSmall` min-height: 52px; `}
`

const InlinePercent = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 2px 6px;
  font-size: 10px;
  line-height: 1;
  background: ${({ theme }) => theme.cardPrimary};
  color: ${({ theme }) => theme.textSecondary};
  min-width: 28px;
`

const PercentSkeleton = styled.div`
  width: 32px;
  height: 16px;
  border-radius: 6px;
  background: ${({ theme }) => theme.cardPrimary};
  animation: shimmer 1.2s ease-in-out infinite;
  @keyframes shimmer {
    0% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.5;
    }
  }
`

interface FeeOptionProps {
  feeAmount: FeeAmount
  active: boolean
  distributions: ReturnType<typeof useFeeTierDistribution>['distributions']
  tvlAmounts?: Record<FeeAmount, number | undefined>
  poolState: PoolState
  onClick: () => void
  isLoading: boolean
}

export function FeeOption({
  feeAmount,
  active,
  poolState,
  distributions,
  tvlAmounts,
  onClick,
  isLoading,
}: FeeOptionProps) {
  const percent = !isLoading ? toDisplayPercent(distributions?.[feeAmount], true) : undefined
  const tvl = tvlAmounts?.[feeAmount]
  const disabled = false
  return (
    <Chip active={active} disabled={disabled} onClick={!disabled ? onClick : undefined} aria-pressed={active}>
      <AutoColumn gap="4px" justify="center" style={{ alignItems: 'center' }}>
        <ThemedText.DeprecatedLabel as="div" style={{ lineHeight: 1 }}>
          <T.BodyS style={{ fontSize: 12 }}>{FEE_AMOUNT_DETAIL[feeAmount].label}%</T.BodyS>
        </ThemedText.DeprecatedLabel>

        {isLoading ? (
          <PercentSkeleton />
        ) : (
          <InlinePercent>{percent != null ? `${Math.round(percent)}%` : '–'}</InlinePercent>
        )}
      </AutoColumn>
    </Chip>
  )
}
